// High-level picto generator composing all three layers
import locationDot from '../assets/images/location-dot-solid.svg';
import {
  ResourcePositionLayer,
  IResourceData,
  IApiCoordinateMarker,
  PictoSizeLayer,
  PictoSizeMode,
  PictoPositioningMode,
  PictoTypeLayer,
  IResourcePictoData,
  IUserPictoData,
  IFinalPicto,
} from './layers';
import { Matrix, IScaleParams } from '../types';

export interface IPictoGeneratorOptions {
  scaleParams: IScaleParams;
  mapIconScale?: number;
  matrix?: Matrix;
  apiMarkers?: IApiCoordinateMarker[];
}

/**
 * High-level picto generator that composes all three layers
 *
 * Usage:
 * ```ts
 * const generator = new PictoGenerator(options);
 * const resourcePictos = generator.generateResourcePictos(resources, resourceData);
 * const bookingPictos = generator.generateBookingPictos(resources, locationDotUrl);
 * const userPictos = generator.generateUserPictos(bookings, userData);
 * ```
 */
export class PictoGenerator {
  private scaleParams: IScaleParams;
  private mapIconScale: number;
  private matrix?: Matrix;
  private apiMarkers?: IApiCoordinateMarker[];

  constructor(options: IPictoGeneratorOptions) {
    this.scaleParams = options.scaleParams;
    this.mapIconScale = options.mapIconScale ?? 3;
    this.matrix = options.matrix;
    this.apiMarkers = options.apiMarkers;
  }

  generateResourceHeatmapPictos(
    resources: IResourceData[],
    resourceData: Map<number, IResourcePictoData>,
    resourceIconSizes: Map<number, number | undefined>
  ): IFinalPicto[] {
    const positions = ResourcePositionLayer.getPositions(resources, this.matrix, this.apiMarkers);

    if (positions.length === 0) {
      return [];
    }

    const configurations = PictoSizeLayer.process(
      positions,
      resourceIconSizes,
      this.scaleParams,
      PictoSizeMode.ResourceBased,
      PictoPositioningMode.Center,
      this.mapIconScale
    );

    return PictoTypeLayer.applyResourceHeatmapType(configurations, resourceData);
  }

  generateResourcePictos(
    resources: IResourceData[],
    resourceData: Map<number, IResourcePictoData>,
    resourceIconSizes: Map<number, number | undefined>
  ): IFinalPicto[] {
    const positions = ResourcePositionLayer.getPositions(resources, this.matrix, this.apiMarkers);

    if (positions.length === 0) {
      return [];
    }

    const configurations = PictoSizeLayer.process(
      positions,
      resourceIconSizes,
      this.scaleParams,
      PictoSizeMode.ResourceBased,
      PictoPositioningMode.Center,
      this.mapIconScale
    );

    return PictoTypeLayer.applyResourceType(configurations, resourceData);
  }

  generateBookingPictos(bookings: Array<{ id: number; resource: IResourceData }> = []): IFinalPicto[] {
    const resources = bookings.map((b) => b.resource);
    const positions = ResourcePositionLayer.getPositions(resources, this.matrix, this.apiMarkers);

    if (positions.length === 0) {
      return [];
    }

    const configurations = PictoSizeLayer.process(
      positions,
      new Map(),
      this.scaleParams,
      PictoSizeMode.Fixed,
      PictoPositioningMode.Pin,
      1
    );

    return PictoTypeLayer.applyBookingType(configurations, locationDot);
  }

  generateUserPictos(
    userData: Map<number, IUserPictoData>,
    bookings: Array<{ id: number; resource: IResourceData }> = []
  ): IFinalPicto[] {
    const resources = bookings.map((b) => b.resource);
    const positions = ResourcePositionLayer.getPositions(resources, this.matrix, this.apiMarkers);

    if (positions.length === 0) {
      return [];
    }

    const configurations = PictoSizeLayer.process(
      positions,
      new Map(),
      this.scaleParams,
      PictoSizeMode.Fixed,
      PictoPositioningMode.Center,
      1
    );

    return PictoTypeLayer.applyUserType(configurations, userData);
  }
}
